/* ============================================================================
   PANEL DE AGREGAR · reglas de las tres pestañas (Pendientes, Catálogo,
   Promociones).

   Fuera del componente porque son lo que hay que poder comprobar sin montar
   React: qué se ve al buscar, qué hace exactamente la franja de acción
   masiva, y cuánto suma lo que se va a agregar.

   NINGUNA REGLA DE NEGOCIO VIVE AQUÍ. Los importes se calculan con la misma
   fórmula de siempre (precio × cantidad, cantidad ausente = 1), la misma que
   usan la tabla de la factura y utils/facturaLote.js.
   ========================================================================== */

/** El id de una prestación pendiente, que es también su id en la factura. */
export function idPendiente(item) {
    return item?.idprocedimiento_cita_paciente;
}

/**
 * Búsqueda por código o descripción, sin acentos ni mayúsculas.
 *
 * Se normalizan las tildes a propósito: el catálogo tiene "ECOGRAFIA" sin
 * tilde y "Ecografía" con ella, y quien busca no debería tener que acertar
 * cuál de las dos está guardada.
 */
const normalizar = (v) =>
    String(v ?? "")
        .toLocaleLowerCase("es")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

export function filtrarPorTexto(items, texto) {
    const t = normalizar(texto).trim();
    if (!t) return items || [];
    return (items || []).filter((item) => {
        const codigo = normalizar(item?.ExamenTipo?.codigo);
        const descripcion = normalizar(item?.ExamenTipo?.descripcion);
        return codigo.includes(t) || descripcion.includes(t);
    });
}

/** Filtro por categoría del examen. `""` o `null` = sin filtro. */
export function filtrarPorCategoria(items, categoriaId) {
    if (categoriaId === "" || categoriaId === null || categoriaId === undefined) return items || [];
    return (items || []).filter(
        (item) => String(item?.ExamenTipo?.categoria_id ?? "") === String(categoriaId)
    );
}

/** Importe de un lote, con la fórmula de siempre. */
export function importeDe(items) {
    return (items || []).reduce((acc, item) => {
        const precio = Number(item?.precio);
        const cantidad = Number(item?.cantidad) || 1;
        return acc + (Number.isFinite(precio) ? precio * cantidad : 0);
    }, 0);
}

/* -- la franja de acción masiva -------------------------------------------
   Refleja los filtros activos y actúa sobre TODOS los resultados del filtro,
   no sobre lo que se ve en pantalla. Cuando ya están todos dentro, cambia a
   "Quitar los N": es la simetría que el encargo llama innegociable.
   ------------------------------------------------------------------------- */

/**
 * @param visibles  los resultados del filtro (todos, no la porción pintada)
 * @param estaDentro (item) => bool -- si ya está en la factura
 * @returns null si no hay nada visible; si no,
 *   { modo: "agregar" | "quitar", cuantas, importe, faltan }
 *
 * `cuantas` es el número que se escribe en el botón y es SIEMPRE el total de
 * resultados del filtro -- lo que el usuario está mirando. `faltan` son los
 * que de verdad se van a despachar al agregar (los que aún no están dentro);
 * los que ya estaban se quedan como estaban, no se duplican.
 */
export function accionMasiva(visibles, estaDentro) {
    const lista = visibles || [];
    if (lista.length === 0) return null;
    const fuera = lista.filter((item) => !estaDentro(item));
    if (fuera.length === 0) {
        return { modo: "quitar", cuantas: lista.length, importe: importeDe(lista), faltan: [] };
    }
    return {
        modo: "agregar",
        cuantas: lista.length,
        importe: importeDe(lista),
        faltan: fuera,
    };
}

/* -- el origen de una prestación pendiente ---------------------------------
   Tres "BIOMETRIA HEMATICA COMPLETA" del mismo paciente son indistinguibles
   sin esto. Misma idea que la columna Origen de la tabla de la factura.
   ------------------------------------------------------------------------- */

export function origenCorto(item) {
    const id = idPendiente(item);
    const cruda = item?.fecha_emision;
    let fecha = null;
    if (cruda) {
        // La fecha llega en UTC ("2025-04-22T00:00:00.000Z"). Formatearla en
        // hora local la corre un día hacia atrás en Ecuador (UTC-5), así que
        // se leen las partes de la propia cadena.
        const m = String(cruda).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) fecha = `${m[3]}/${m[2]}/${m[1]}`;
    }
    const partes = [];
    if (id !== undefined && id !== null) partes.push(`#${id}`);
    if (fecha) partes.push(fecha);
    return partes.join(" · ");
}

/** Texto del contador del buscador: "6 de 380". Vacío si no se está buscando. */
export function contadorCoincidencias(texto, visibles, total) {
    if (!String(texto ?? "").trim()) return "";
    return `${visibles} de ${total}`;
}

/* ============================================================================
   PESTAÑA CATÁLOGO

   Las filas son del tarifario (`procedimiento_company_precios`), no
   prestaciones: tienen otro id, no tienen fecha ni número de orden, y marcar
   NO las mete en la factura -- las selecciona. Agregarlas crea filas reales en
   la base, así que va en una sola llamada al pulsar el botón.
   ========================================================================== */

/** El id de una línea del tarifario. NO es el de una prestación. */
export function idTarifa(item) {
    return item?.idprocedimiento_company_precios;
}

/** Filtro por tipo de examen (LABORATORIO, RADIOLOGIA...). `""` = sin filtro. */
export function filtrarPorTipo(items, tipo) {
    if (!tipo) return items || [];
    return (items || []).filter((item) => item?.ExamenTipo?.tipo === tipo);
}

/**
 * Tipos presentes en el lote, ordenados alfabéticamente, para el desplegable.
 * Sólo los que existen de verdad: ofrecer "TOMOGRAFIA" cuando el tarifario de
 * esa aseguradora no tiene ninguna es ofrecer un filtro que no filtra nada.
 */
export function tiposDe(items) {
    const vistos = new Set();
    (items || []).forEach((item) => {
        const t = item?.ExamenTipo?.tipo;
        if (typeof t === "string" && t.trim() !== "") vistos.add(t.trim());
    });
    return [...vistos].sort((a, b) => a.localeCompare(b, "es"));
}

/**
 * El cuerpo EXACTO que espera POST /billing/procedures/news.
 *
 * Está aquí, y no dentro del componente, para poder fijarlo con una prueba:
 * es el único punto de este rediseño que escribe en la base, y su forma es
 * la misma que enviaba el modal viejo, campo por campo.
 */
export function payloadProcedimientosNuevos(pacienteId, items) {
    return {
        paciente_id: pacienteId,
        procedures: (items || []).map((item) => ({
            examen_tipo_id: item.idExamenTipo,
            precio: item.precio,
            seguro_id: item.seguro_id,
        })),
    };
}

/**
 * ¿Este examen del catálogo ya está en la factura?
 *
 * Se compara por `idExamenTipo`, que es lo único que comparten una línea del
 * tarifario y una prestación ya agregada. Es INFORMATIVO, no bloqueante: el
 * mismo examen se puede facturar dos veces de forma legítima (dos ecografías
 * distintas del mismo tipo), así que la etiqueta avisa y no impide nada.
 */
export function yaFacturado(item, idsExamenEnFactura) {
    return idsExamenEnFactura.has(item?.idExamenTipo);
}

/* ============================================================================
   PESTAÑA PROMOCIONES

   Una promoción no es un procedimiento: es un GRUPO. La mayor del sistema trae
   44 exámenes. Por eso la fila se expande (para ver qué entra antes de
   agregarlo) y por eso se elige UNA, no varias -- el modal viejo rechazaba
   explícitamente más de una y esa regla no se toca aquí.
   ========================================================================== */

export function idPromocion(promo) {
    return promo?.id_promocion;
}

/** Los exámenes que trae la promoción. Nunca `undefined`. */
export function detallesDe(promo) {
    return Array.isArray(promo?.detalles) ? promo.detalles : [];
}

/** Búsqueda por el NOMBRE de la promoción, no por sus exámenes. */
export function filtrarPromociones(promos, texto) {
    const t = normalizar(texto).trim();
    if (!t) return promos || [];
    return (promos || []).filter((p) => normalizar(p?.nombre).includes(t));
}

/**
 * Lo que costará la promoción: la suma de `precio_promocion` de sus exámenes.
 * NO se recalcula nada -- es el precio pactado que ya está guardado.
 */
export function importePromocion(promo) {
    return detallesDe(promo).reduce((acc, d) => {
        const p = Number(d?.precio_promocion);
        return acc + (Number.isFinite(p) ? p : 0);
    }, 0);
}

/**
 * El cuerpo EXACTO que enviaba el modal viejo para una promoción.
 *
 * Ojo a las diferencias con el del catálogo, que son reales y no un descuido:
 * el examen viene en `id_tipo_examen` (no `idExamenTipo`), el precio en
 * `precio_promocion`, el seguro sale de la CABECERA de la promoción, y va un
 * `factura_promocion: 1` que marca la prestación como nacida de una promoción.
 */
export function payloadPromocion(pacienteId, promo) {
    return {
        paciente_id: pacienteId,
        procedures: detallesDe(promo).map((d) => ({
            examen_tipo_id: d.id_tipo_examen,
            precio: d.precio_promocion,
            seguro_id: promo.seguro_id,
            factura_promocion: 1,
        })),
    };
}
